import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {getNetPromoterScoresStatus} from '../Utils/Apis/netPromoterScoreApis';
import {RootState} from './Store';

interface SubmissionResponse {
  has_recent_activity?: boolean;
  has_submitted_in_last_30_days?: boolean;
  cancellation_count?: number;
  can_cancel?: boolean;
}

// Shape of slice state
interface SubmissionState {
  isSubmitted: boolean;
  hasSubmittedInLast30Days: boolean;
  cancellationCount: number;
  canCancel: boolean;
  loading: boolean;
  error: string | null;
  authTime: number | null;
  npsDisplayed: boolean;
  statusFetchedAt: number | null;
  statusFetchedForEmail: string | null;
}

// Initial state
const initialState: SubmissionState = {
  isSubmitted: false,
  hasSubmittedInLast30Days: false,
  cancellationCount: 0,
  canCancel: true,
  loading: false,
  error: null,
  authTime: null,
  npsDisplayed: false,
  statusFetchedAt: null,
  statusFetchedForEmail: null,
};

const STATUS_TTL_MS = 5 * 60 * 1000;

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const fetchSubmission = createAsyncThunk<
  SubmissionResponse,
  string,
  {rejectValue: string}
>(
  'npsSubmissionSlice/fetchSubmission',
  async (userEmail, {rejectWithValue}) => {
    try {
      const res = await getNetPromoterScoresStatus(userEmail);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'API Error');
    }
  },
  {
    condition: (userEmail, {getState}) => {
      const state = getState() as RootState;
      const normalizedEmail = normalizeEmail(userEmail || '');

      if (!normalizedEmail || state.npsSlice.loading) {
        return false;
      }

      const sameEmail =
        state.npsSlice.statusFetchedForEmail === normalizedEmail;
      const fetchedRecently =
        !!state.npsSlice.statusFetchedAt &&
        Date.now() - state.npsSlice.statusFetchedAt < STATUS_TTL_MS;

      if (sameEmail && fetchedRecently) {
        return false;
      }

      return true;
    },
  },
);

const NpsSubmissionSlice = createSlice({
  name: 'npsSubmissionSlice',
  initialState,
  reducers: {
    resetSubmission: (state) => {
      state.isSubmitted = false;
      state.hasSubmittedInLast30Days = false;
      state.cancellationCount = 0;
      state.canCancel = true;
      state.error = null;
      state.loading = false;
      state.authTime = null;
      state.npsDisplayed = false;
      state.statusFetchedAt = null;
      state.statusFetchedForEmail = null;
    },
    setAuthTime: (state, action) => {
      state.authTime = action.payload;
    },
    setNpsDisplayed: (state, action) => {
      state.npsDisplayed = action.payload;
    },
    setSubmitted: (state, action) => {
      state.isSubmitted = action.payload.isSubmitted;
    },
    setNpsStatus: (state, action) => {
      if (typeof action.payload?.canCancel === 'boolean') {
        state.canCancel = action.payload.canCancel;
      }

      if (typeof action.payload?.cancellationCount === 'number') {
        state.cancellationCount = action.payload.cancellationCount;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubmission.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const hasSubmittedInLast30Days =
            action.payload?.has_submitted_in_last_30_days ??
            action.payload?.has_recent_activity ??
            false;

          state.hasSubmittedInLast30Days = hasSubmittedInLast30Days;
          state.isSubmitted = hasSubmittedInLast30Days;
          state.cancellationCount = action.payload?.cancellation_count ?? 0;
          state.canCancel = action.payload?.can_cancel ?? true;
          state.statusFetchedAt = Date.now();
          state.statusFetchedForEmail = normalizeEmail(action.meta.arg || '');
        }
      })
      .addCase(fetchSubmission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Something went wrong';
      });
  },
});

export const {
  resetSubmission,
  setAuthTime,
  setNpsDisplayed,
  setSubmitted,
  setNpsStatus,
} = NpsSubmissionSlice.actions;
export const getIsSubmitted = (state: RootState) => state.npsSlice.isSubmitted;
export const getAuthTime = (state: RootState) => state.npsSlice.authTime;
export const getLoading = (state: RootState) => state.npsSlice.loading;
export const getNpsDisplayed = (state: RootState) =>
  state.npsSlice.npsDisplayed;
export const getHasSubmittedInLast30Days = (state: RootState) =>
  state.npsSlice.hasSubmittedInLast30Days;
export const getCanCancel = (state: RootState) => state.npsSlice.canCancel;
export const getCancellationCount = (state: RootState) =>
  state.npsSlice.cancellationCount;

export default NpsSubmissionSlice.reducer;
